import React, { useCallback, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Chip,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import MotorcycleIcon from '@mui/icons-material/TwoWheeler';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { toast } from 'react-toastify';
import { api } from '../services/AuthService';
import { useAuth } from '../services/AuthService';

function Motoboys({ payoutsOnly = false }) {
  const [motoboys, setMotoboys] = useState([]);
  const [stripeStatus, setStripeStatus] = useState({});
  const [stripeBusy, setStripeBusy] = useState('');
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [payoutsConfigured, setPayoutsConfigured] = useState(false);
  const [payoutBusyId, setPayoutBusyId] = useState('');
  const [revealedPix, setRevealedPix] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMotoboy, setEditingMotoboy] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    motoboy_scope: 'APP',
    service_city: '',
    vehicle_plate: '',
    vehicle_type: 'moto',
    available: true
  });
  const { isAdminMaster, isAdminLanchonete, user } = useAuth();
  const isMaster = user?.user_type === 'admin_master';

  const loadMotoboys = useCallback(async () => {
    let courierList = [];
    try {
      const response = await api.get('/motoboys');
      courierList = Array.isArray(response.data) ? response.data : [];
      setMotoboys(courierList);
    } catch (error) {
      if (!payoutsOnly) toast.error('Erro ao carregar motoboys');
      console.error('Erro ao carregar motoboys:', error);
    }

    if (!isMaster) return;

    // A lista de saques é carregada independentemente da lista geral de entregadores.
    try {
      const payoutResponse = await api.get('/motoboys/payouts/requests');
      setPayoutRequests(payoutResponse.data.requests || []);
      setPayoutsConfigured(Boolean(payoutResponse.data.payouts_configured));
    } catch (error) {
      console.error('Erro ao carregar solicitações de saque:', error);
    }

    const statuses = await Promise.all(courierList.map(async (motoboy) => {
      const isStoreCourier = Boolean(motoboy.lanchonete_id) || String(motoboy.motoboy_scope || '').toUpperCase() === 'LOJA';
      if (isStoreCourier) return [motoboy.id, { storeCourier: true }];
      try {
        const status = await api.get(`/stripe-production/accounts/motoboy/${motoboy.id}/status`);
        return [motoboy.id, status.data];
      } catch (error) {
        return [motoboy.id, { connected: false, ready: false, unavailable: true }];
      }
    }));
    setStripeStatus(Object.fromEntries(statuses));
  }, [isMaster, payoutsOnly]);

  useEffect(() => {
    loadMotoboys();
  }, [loadMotoboys]);

  const loadPayoutRequests = useCallback(async (showError = true) => {
    try {
      const response = await api.get('/motoboys/payouts/requests');
      setPayoutRequests(response.data.requests || []);
      setPayoutsConfigured(Boolean(response.data.payouts_configured));
    } catch (error) {
      if (showError) toast.error(error.response?.data?.error || 'Não foi possível atualizar as solicitações de saque');
    }
  }, []);

  useEffect(() => {
    if (!payoutsOnly || !isMaster) return undefined;
    const timer = window.setInterval(() => loadPayoutRequests(false), 15000);
    return () => window.clearInterval(timer);
  }, [payoutsOnly, isMaster, loadPayoutRequests]);

  const handlePayoutAction = async (payout, action) => {
    const pay = action === 'pay';
    const prompt = pay
      ? `Agendar Pix de R$ ${Number(payout.amount).toFixed(2)} para ${payout.motoboy_name}, referente a ${payout.delivery_count} entrega(s), para 00:00? O saldo do Mercado Pago precisa estar disponível. O horário de crédito depende da confirmação do provedor e banco de destino.`
      : `Cancelar esta solicitação de R$ ${Number(payout.amount).toFixed(2)}? As entregas voltarão ao saldo disponível do motoboy.`;
    if (!window.confirm(prompt)) return;
    try {
      setPayoutBusyId(String(payout.id));
      const result = await api.post(`/motoboys/payouts/requests/${payout.id}/${pay ? 'pay' : 'reject'}`);
      toast.success(result.data.message || (pay ? 'Pix enviado para agendamento' : 'Solicitação cancelada'));
      await loadPayoutRequests();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Não foi possível processar esta solicitação');
    } finally {
      setPayoutBusyId('');
    }
  };

  const handleRefreshPayout = async (payout) => {
    try {
      setPayoutBusyId(String(payout.id));
      await api.post(`/motoboys/payouts/requests/${payout.id}/refresh`);
      await loadPayoutRequests();
      toast.success('Status consultado no Mercado Pago');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Não foi possível consultar o pagamento');
    } finally {
      setPayoutBusyId('');
    }
  };

  const handleRevealPayoutPix = async (payout) => {
    try {
      setPayoutBusyId(String(payout.id));
      const response = await api.get(`/motoboys/payouts/requests/${payout.id}/pix-key`);
      setRevealedPix({ ...response.data, payout_id: payout.id });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Não foi possível exibir a chave Pix');
    } finally {
      setPayoutBusyId('');
    }
  };

  const handleConfirmManualPayout = async (payout) => {
    const confirmed = window.confirm(
      `Você já concluiu o Pix de R$ ${Number(payout.amount).toFixed(2)} para ${payout.motoboy_name}? Só confirme depois de ver a transferência concluída no app Mercado Pago. Esta ação zera o saldo pendente do motoboy.`
    );
    if (!confirmed) return;
    const paymentReference = window.prompt('ID ou observação do comprovante (opcional):', '') || '';
    try {
      setPayoutBusyId(String(payout.id));
      const result = await api.post(`/motoboys/payouts/requests/${payout.id}/manual-paid`, { payment_reference: paymentReference });
      setRevealedPix(null);
      toast.success(result.data.message || 'Pagamento manual confirmado');
      await loadPayoutRequests();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Não foi possível confirmar o pagamento manual');
    } finally {
      setPayoutBusyId('');
    }
  };

  const handleCopyPixKey = async () => {
    try {
      await navigator.clipboard.writeText(revealedPix.pix_key);
      toast.success('Chave Pix copiada');
    } catch (error) {
      toast.error('Não foi possível copiar; selecione a chave e copie manualmente');
    }
  };

  const getPayoutStatusLabel = (status) => ({
    requested: 'Aguardando Admin Master', processing: 'Em conferência no Mercado Pago',
    scheduled: 'Agendado para 00:00', paid: 'Pago', failed: 'Falhou — saldo liberado',
    cancelled: 'Cancelado'
  })[status] || status;

  const handleOpenDialog = (motoboy = null) => {
    if (motoboy) {
      setEditingMotoboy(motoboy);
      setFormData({
        name: motoboy.name,
        email: motoboy.email,
        password: '',
        phone: motoboy.phone,
        motoboy_scope: motoboy.motoboy_scope || 'APP',
        service_city: motoboy.service_city || '',
        vehicle_plate: motoboy.vehicle_plate,
        vehicle_type: motoboy.vehicle_type,
        available: motoboy.available
      });
    } else {
      setEditingMotoboy(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        motoboy_scope: 'APP',
        service_city: '',
        vehicle_plate: '',
        vehicle_type: 'moto',
        available: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMotoboy(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = { ...formData };
      
      if (editingMotoboy) {
        // Editando motoboy existente
        if (!submitData.password) {
          delete submitData.password;
        }
        await api.put(`/users/${editingMotoboy.id}`, submitData);
        await api.put(`/motoboys/${editingMotoboy.id}`, {
          vehicle_plate: submitData.vehicle_plate,
          vehicle_type: submitData.vehicle_type,
          available: submitData.available
        });
        toast.success('Motoboy atualizado com sucesso!');
      } else {
        // Criando novo motoboy
        if (!submitData.password) {
          toast.error('Senha é obrigatória para novos motoboys');
          return;
        }
        
        const userResponse = await api.post('/users/motoboy', {
          name: submitData.name,
          email: submitData.email,
          password: submitData.password,
          phone: submitData.phone,
          motoboy_scope: 'APP',
          service_city: submitData.service_city,
          lanchonete_id: null,
          available: submitData.available
        });
        
        console.log('✅ Usuário criado:', userResponse.data);
        
        toast.success('Motoboy criado com sucesso!');
      }
      
      handleCloseDialog();
      loadMotoboys();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar motoboy');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este motoboy?')) {
      try {
        await api.delete(`/users/${id}`);
        toast.success('Motoboy excluído com sucesso!');
        loadMotoboys();
      } catch (error) {
        toast.error('Erro ao excluir motoboy');
      }
    }
  };

  const handleToggleStatus = async (id, available) => {
    try {
      await api.patch(`/motoboys/${id}/status`, { available });
      toast.success(`Motoboy ${available ? 'ativado' : 'desativado'} com sucesso!`);
      loadMotoboys();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleStripeOnboarding = async (motoboy) => {
    try {
      setStripeBusy(String(motoboy.id));
      const current = stripeStatus[motoboy.id];
      const response = current?.connected
        ? await api.post(`/stripe-production/accounts/motoboy/${motoboy.id}/onboarding-link`, { return_path: '/motoboys' })
        : await api.post('/stripe-production/accounts', { entity_type: 'motoboy', entity_id: motoboy.id, return_path: '/motoboys' });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao abrir cadastro Stripe do motoboy');
      setStripeBusy('');
    }
  };

  const getVehicleTypeText = (type) => {
    switch (type) {
      case 'moto': return 'Motocicleta';
      case 'carro': return 'Carro';
      case 'bike': return 'Bicicleta';
      default: return type;
    }
  };

  if (!isAdminMaster() && !isAdminLanchonete()) {
    return (
      <Alert severity="warning">
        Acesso restrito. Apenas o administrador master ou administrador de lanchonete podem gerenciar motoboys.
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          {payoutsOnly ? 'Saques dos Motoboys' : 'Motoboys'}
        </Typography>
        {!payoutsOnly && <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Novo Motoboy
        </Button>}
      </Box>

      {isAdminMaster() && (
        <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
            <Box>
              <Typography variant="h6" fontWeight="bold">Saques dos motoboys do APP</Typography>
              <Typography variant="body2" color="text.secondary">Solicitações reservam as entregas. O saldo só zera depois da confirmação automática do Mercado Pago ou de você confirmar que já fez o Pix manualmente.</Typography>
            </Box>
            <Button onClick={loadPayoutRequests}>Atualizar</Button>
          </Box>
          {!payoutsConfigured && <Alert severity="info" sx={{ my: 2 }}>Payouts automático não está disponível nesta conta. O motoboy ainda pode pedir o saque: a previsão informada é às 00:00. Faça o Pix pelo app Mercado Pago e confirme aqui somente depois que a transferência estiver concluída.</Alert>}
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Motoboy APP</TableCell><TableCell>Entregas</TableCell><TableCell>Valor</TableCell>
                <TableCell>Pix</TableCell><TableCell>Status</TableCell><TableCell>Solicitado</TableCell><TableCell>Ações</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {payoutRequests.length === 0 ? <TableRow><TableCell colSpan={7} align="center">Nenhuma solicitação de saque.</TableCell></TableRow> : payoutRequests.map((payout) => (
                  <TableRow key={payout.id}>
                    <TableCell>{payout.motoboy_name}<br /><Typography variant="caption" color="text.secondary">#{payout.motoboy_id}</Typography></TableCell>
                    <TableCell>{payout.delivery_count}</TableCell>
                    <TableCell>R$ {Number(payout.amount).toFixed(2)}</TableCell>
                    <TableCell>{payout.pix_key_type} · {payout.pix_key_masked}</TableCell>
                    <TableCell><Chip size="small" label={payout.status === 'paid' && payout.payment_method === 'manual_mp' ? 'Pago manual no MP' : getPayoutStatusLabel(payout.status)} color={payout.status === 'paid' ? 'success' : payout.status === 'failed' ? 'error' : 'default'} />{payout.payment_reference && <Typography variant="caption" display="block">Ref.: {payout.payment_reference}</Typography>}{payout.error_message && <Typography variant="caption" display="block" color="error">{payout.error_message}</Typography>}</TableCell>
                    <TableCell>{payout.requested_at ? new Date(payout.requested_at).toLocaleString('pt-BR') : '—'}</TableCell>
                    <TableCell>
                      {payout.status === 'requested' && <Button size="small" disabled={payoutBusyId === String(payout.id)} onClick={() => handleRevealPayoutPix(payout)}>Ver Pix</Button>}
                      {(payout.status === 'requested' || (payout.status === 'processing' && !payout.mp_payout_id)) && payoutsConfigured && <Button size="small" variant="contained" disabled={payoutBusyId === String(payout.id)} onClick={() => handlePayoutAction(payout, 'pay')}>{payout.status === 'processing' ? 'Reenviar com segurança' : 'Pagar automático'}</Button>}
                      {payout.status === 'requested' && <Button size="small" variant="outlined" color="success" disabled={payoutBusyId === String(payout.id)} onClick={() => handleConfirmManualPayout(payout)}>Já paguei manualmente</Button>}
                      {payout.status === 'requested' && <Button size="small" color="inherit" disabled={payoutBusyId === String(payout.id)} onClick={() => handlePayoutAction(payout, 'reject')}>Cancelar</Button>}
                      {['scheduled', 'processing'].includes(payout.status) && payout.mp_payout_id && <Button size="small" disabled={payoutBusyId === String(payout.id)} onClick={() => handleRefreshPayout(payout)}>Conferir status</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={Boolean(revealedPix)} onClose={() => setRevealedPix(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Chave Pix de {revealedPix?.motoboy_name}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>Confira nome e valor no Mercado Pago antes de transferir.</Alert>
          <TextField fullWidth label={`Chave Pix (${revealedPix?.pix_key_type})`} value={revealedPix?.pix_key || ''} InputProps={{ readOnly: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevealedPix(null)}>Fechar</Button>
          <Button onClick={handleCopyPixKey} variant="contained" disabled={!revealedPix?.pix_key}>Copiar chave</Button>
        </DialogActions>
      </Dialog>

      {!payoutsOnly && <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Cidade de atendimento</TableCell>
              <TableCell>Veículo</TableCell>
              <TableCell>Placa</TableCell>
              <TableCell>Status</TableCell>
              {isAdminMaster() && <TableCell>Recebimento Stripe</TableCell>}
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {motoboys.map((motoboy) => (
              <TableRow key={motoboy.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <PersonIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      {motoboy.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {motoboy.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2">{motoboy.phone}</Typography>
                  </Box>
                </TableCell>
                <TableCell>{motoboy.service_city || 'Não definida'}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <MotorcycleIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      {getVehicleTypeText(motoboy.vehicle_type)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {motoboy.vehicle_plate || 'Não informada'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={motoboy.available}
                          onChange={(e) => handleToggleStatus(motoboy.id, e.target.checked)}
                          color="primary"
                        />
                      }
                      label=""
                    />
                    <Chip
                      icon={motoboy.available ? <CheckCircleIcon /> : <LocalShippingIcon />}
                      label={motoboy.available ? 'Disponível' : 'Indisponível'}
                      color={motoboy.available ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </TableCell>
                {isAdminMaster() && (
                  <TableCell>
                    {stripeStatus[motoboy.id]?.storeCourier ? (
                      <Chip size="small" label="Recebe pela lanchonete" />
                    ) : (
                      <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                        <Chip
                          size="small"
                          color={stripeStatus[motoboy.id]?.ready ? 'success' : 'warning'}
                          label={stripeStatus[motoboy.id]?.ready ? 'Stripe pronta' : stripeStatus[motoboy.id]?.connected ? 'Cadastro pendente' : 'Não conectado'}
                        />
                        {!stripeStatus[motoboy.id]?.ready && (
                          <Button size="small" variant="outlined" disabled={Boolean(stripeBusy)} onClick={() => handleStripeOnboarding(motoboy)}>
                            {stripeStatus[motoboy.id]?.connected ? 'Continuar cadastro' : 'Conectar Stripe'}
                          </Button>
                        )}
                      </Box>
                    )}
                  </TableCell>
                )}
                <TableCell align="right">
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(motoboy)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(motoboy.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>}

      {/* Dialog de criação/edição */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMotoboy ? 'Editar Motoboy' : 'Novo Motoboy'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                name="name"
                label="Nome"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                name="password"
                label="Senha"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                helperText={editingMotoboy ? "Deixe em branco para manter a senha atual" : ""}
                required={!editingMotoboy}
              />
              <TextField
                name="phone"
                label="Telefone"
                value={formData.phone}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                name="service_city"
                label="Cidade de atendimento"
                value={formData.service_city}
                onChange={handleInputChange}
                fullWidth
                required
                helperText="As corridas do aplicativo serão enviadas a motoboys desta cidade."
              />
              <TextField
                name="vehicle_plate"
                label="Placa do Veículo"
                value={formData.vehicle_plate}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <FormControl fullWidth>
                <InputLabel>Tipo de Veículo</InputLabel>
                <Select
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleInputChange}
                  label="Tipo de Veículo"
                >
                  <MenuItem value="moto">Motocicleta</MenuItem>
                  <MenuItem value="carro">Carro</MenuItem>
                  <MenuItem value="bike">Bicicleta</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    name="available"
                    checked={formData.available}
                    onChange={handleInputChange}
                    color="primary"
                  />
                }
                label="Motoboy Disponível"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingMotoboy ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Motoboys;
